import { QueryBuilder } from "@vortechron/query-builder-ts";
import { Button } from "antd";
import { useGetFormFieldQuery, useBatchUpdateFormFieldsMutation } from "api/TenantSlug/FormField/hooks.ts";
import { useGetFormStructureQuery } from "api/TenantSlug/FormStructure/hooks.ts";
import { COMMON_GET_FORM_STRUCTURE_QUERY_FILTERS } from "api/TenantSlug/FormStructure/queryOptions.ts";
import { FormFieldParentFieldSelector } from "components/Drawers/FormFieldParentFieldDrawer/FormFieldParentFieldSelector.tsx";
import { Drawer } from "components/UI/Drawer";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { DrawerId } from "types/models.ts";
import { z } from "zod";

import FormFieldData = App.Data.FormFieldData;
import FormStructureData = App.Data.FormStructureData;
import UpdateFormFieldRequest = App.Http.Requests.FormField.UpdateFormFieldRequest;

interface FormFieldParentFieldDrawerProps {
    formFieldId: string;
}

export const FormFieldParentFieldDrawer = ({ formFieldId, ...props }: FormFieldParentFieldDrawerProps) => {
    const { t } = usePaldockTranslation("drawers.form-field-parent-field");
    const { t: tC } = usePaldockTranslation("common");
    const id = DrawerId.ParentField;
    const { hide } = useDrawer();
    const [formField, setFormField] = useState<FormFieldData | null>(null);
    const [formStructure, setFormStructure] = useState<FormStructureData | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const batchUpdateFormFieldsMutation = useBatchUpdateFormFieldsMutation();

    const getFormFieldQuery = useGetFormFieldQuery(
        formFieldId,

        {
            enabled: false,
        }
    );

    // TODO REFACTOR AFTER MERGE OF REFACTORED API
    const queryBuilder = new QueryBuilder("");
    const queryParams = queryBuilder.include("formFields");
    const params: Record<string, string> = queryParams.buildAsArray().reduce(
        (acc, item) => {
            const [key, value] = item.split("=");
            acc[key] = value;
            return acc;
        },
        {} as Record<string, string>
    );

    const getFormStructureQuery = useGetFormStructureQuery(
        formField?.form_structure_id || "",
        {
            ...params,
            ...COMMON_GET_FORM_STRUCTURE_QUERY_FILTERS,
        },
        {
            enabled: false,
        }
    );

    useEffect(() => {
        const fetchFormField = async () => {
            try {
                const response = await getFormFieldQuery.refetch({
                    throwOnError: true,
                });
                if (response && response.data && response.data.formField) {
                    setFormField(response.data.formField);
                    setSelectedId(response.data.formField.parent_form_field_id);
                }
            } catch (error) {
                console.log("refetch error", error);
            }
        };
        if (formFieldId) {
            fetchFormField();
        }
    }, [formFieldId]);

    useEffect(() => {
        const fetchFormStructure = async () => {
            try {
                const response = await getFormStructureQuery.refetch({
                    throwOnError: true,
                });
                if (response && response.data && response.data.formStructure) {
                    setFormStructure(response.data.formStructure);
                }
            } catch (error) {
                console.log("refetch error", error);
            }
        };
        if (formField?.form_structure_id) {
            fetchFormStructure();
        }
    }, [formField]);

    const handleSubmit = () => {
        if (!formField?.id) {
            toast.error(t("messages.form_field_id_required"));
            return;
        }

        const payload: UpdateFormFieldRequest = {
            id: formField.id,
            parent_form_field_id: selectedId ?? null,
            form_structure_id: formField.form_structure_id,
            validation_scenarios: [],
            modification_scenarios: [],
            autocomplete_scenarios: [],
        };

        try {
            batchUpdateFormFieldsMutation.mutate(
                { formFields: [payload] },
                {
                    onSuccess: () => {
                        toast.success(t("messages.updated_success"));
                        hide(id, `${id}-formFieldId`);
                    },
                    onError: () => {
                        toast.error(t("messages.update_error"));
                    },
                }
            );
        } catch (error) {
            if (error instanceof z.ZodError) {
                error.errors.forEach((err) => {
                    toast.error(err.message);
                });
            } else {
                toast.error(t("messages.update_error"));
            }
        }
    };

    if (!formField || !formStructure) {
        return <></>;
    }

    return (
        <Drawer
            drawerId={id}
            title={<DrawerTitle mainTitle={t("title", { fieldName: formField?.system_name })} />}
            searchParamsToClean={[`${id}-formFieldId`]}
            {...props}
        >
            <FormFieldParentFieldSelector
                formField={formField}
                parentFormFields={formStructure?.formFields ?? []}
                handleClick={setSelectedId}
                activeItem={selectedId || null}
            />

            <Button type="primary" onClick={handleSubmit} className="mt-5">
                {tC("save")}
            </Button>
        </Drawer>
    );
};
